interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  kind: 'success' | 'error';
  text: string;
  /** CSS value applied to `bottom` so each caller can clear its own chrome
   *  (reply bar, FAB, tab bar). */
  bottomOffset: string;
  /** Optional trailing pill button — Undo on success, Retry on error. */
  action?: ToastAction;
}

const successIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const errorIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
    <line x1="12" y1="9" x2="12" y2="13" />
    <circle cx="12" cy="17" r="0.5" fill="white" />
  </svg>
);

/**
 * Stripe/Linear-style bottom pill toast. Auto-dismiss is owned by the caller
 * (each view clears its own toast state on a timer); this component is purely
 * presentational so a single markup definition stays in one place.
 */
export function Toast({ kind, text, bottomOffset, action }: ToastProps) {
  return (
    <div
      role="status"
      className="fixed left-1/2 z-40 -translate-x-1/2 animate-ss-slide-up"
      style={{ bottom: bottomOffset }}
    >
      <div className="flex items-center gap-2 rounded-full bg-ink-900/95 px-4 py-2.5 text-[13px] font-semibold text-white shadow-ss-md backdrop-blur-md">
        <span
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: kind === 'success' ? '#10B981' : '#F59E0B' }}
          aria-hidden="true"
        >
          {kind === 'success' ? successIcon : errorIcon}
        </span>
        <span>{text}</span>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="ss-press ml-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-white/25"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
